import { useCallback } from "react";

const referencesCol1 = [
  { label: "Beach Food Festival", href: "https://www.vevey-streatfoodfest.ch/fr/vevey/Accueil/Bienvenue-au-Vevey-StrEAT-Food-Festival-2025.html" },
  { label: "Vevey Noel", href: "https://www.veveynoel.ch/" },
  { label: "Riviera Noel", href: "https://www.veveyrivieranoel.com/" },
  { label: "Ville de Vevey" },
  { label: "Amis de la FeVi", href: "https://www.fetedesvignerons.ch/amisdelafevi/" },
];

const referencesCol2 = [
  { label: "Confrérie des Vignerons", href: "https://www.confreriedesvignerons.ch" },
  { label: "Légurivera", href: "https://www.leguriviera-groupe.ch/" },
  { label: "Paradise" },
  { label: "Socialife", href: "https://socialife.ch/" },
];

export default function Footer() {
  const handleCookieReset = useCallback(() => {
    const fn = (window as unknown as Record<string, (() => void) | undefined>).__resetCookieConsent;
    if (fn) fn();
  }, []);

  return (
    <footer className="relative py-16 md:py-24 bg-background-50">
      {/* Refined top separator */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-300/25 to-transparent" />

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10">
        {/* === 3-column grid === */}
        <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr_320px] gap-12 lg:gap-16 items-start">

          {/* ========== LEFT: Contact ========== */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
            <h4 className="font-playfair text-xs tracking-[0.2em] uppercase text-terracotta mb-7">
              Contact
            </h4>
            <ul className="space-y-5 text-foreground-600 text-sm">
              <li>
                <a
                  href="tel:+41793275134"
                  className="inline-flex items-center gap-3 group cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-hover:text-primary-500 transition-colors duration-300">
                    <i className="ri-phone-line text-base" />
                  </span>
                  <span className="group-hover:text-primary-500 transition-colors duration-300">
                    +41 79 327 51 34
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:lou@laloumaestra.ch"
                  className="inline-flex items-center gap-3 group cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-400 group-hover:text-primary-500 transition-colors duration-300">
                    <i className="ri-mail-line text-base" />
                  </span>
                  <span className="group-hover:text-primary-500 transition-colors duration-300">
                    lou@laloumaestra.ch
                  </span>
                </a>
              </li>
              <li className="inline-flex items-center gap-3">
                <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className="ri-map-pin-line text-base" />
                </span>
                <span>Vevey, Suisse</span>
              </li>
            </ul>
          </div>

          {/* ========== CENTER: Logo ========== */}
          <div className="flex flex-col items-center text-center">
            <img
              src="https://storage.readdy-site.link/project_files/90a975e3-fa94-47ff-988b-c34c350fc03a/cf22ba6a-2d92-4c72-b79c-293366647b08_Lalou-MaestraLogo-P.F.T.png?v=05391ca777dd9038fc82aa7d21e123ba"
              alt="Lalou Maestra"
              className="h-16 md:h-20 w-auto object-contain"
            />

            {/* Signature phrase */}
            <p className="mt-5 text-foreground-500 text-sm leading-relaxed max-w-sm font-playfair italic">
              Coordination et création d&apos;événements publics, gastronomiques et corporate en Suisse romande depuis plus de <span className="font-bold not-italic text-terracotta">10 ans.</span>
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://www.instagram.com/lou_matthey/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-foreground-200 text-foreground-400 hover:text-primary-500 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-instagram-line text-lg" />
              </a>
              <a
                href="https://www.linkedin.com/in/lou-vanessa-matthey-07a1781a3/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-foreground-200 text-foreground-400 hover:text-primary-500 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-linkedin-fill text-lg" />
              </a>
            </div>

            {/* Copyright + Credits */}
            <div className="mt-8 pt-6 border-t border-background-200 w-full max-w-xs">
              <p className="text-xs text-foreground-400 tracking-wide">
                &copy; 2025 Lalou Maestra. Tous droits réservés.
              </p>
              <p className="text-[11px] text-foreground-300 mt-1.5 tracking-wide">
                Site réalisé par Manova Digital
              </p>
              <button
                onClick={handleCookieReset}
                className="mt-3 text-[11px] text-foreground-300 hover:text-primary-500 transition-colors cursor-pointer tracking-wide"
              >
                Gérer les cookies
              </button>
            </div>
          </div>

          {/* ========== RIGHT: Références ========== */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
            <h4 className="font-playfair text-xs tracking-[0.2em] uppercase text-terracotta mb-7">
              Références
            </h4>
            <div className="grid grid-cols-2 gap-x-5 gap-y-3">
              <ul className="space-y-3 text-foreground-600 text-sm">
                {referencesCol1.map((ref) => (
                  <li key={ref.label}>
                    {ref.href ? (
                      <a
                        href={ref.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary-500 transition-colors duration-300 cursor-pointer"
                      >
                        {ref.label}
                      </a>
                    ) : (
                      <span>{ref.label}</span>
                    )}
                  </li>
                ))}
              </ul>
              <ul className="space-y-3 text-foreground-600 text-sm">
                {referencesCol2.map((ref) => (
                  <li key={ref.label}>
                    {ref.href ? (
                      <a
                        href={ref.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-primary-500 transition-colors duration-300 cursor-pointer"
                      >
                        {ref.label}
                      </a>
                    ) : (
                      <span>{ref.label}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}